import React, { useEffect } from 'react';
import AppBar from '../AppBar';
import { Routes, Route, useMatch } from 'react-router-dom';
import AppLoader from './AppLoader';
import Account from '../Account';
import Project from '../Project';
import Dashboard from '../Dashboard';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/useStores';
import type { IProjects } from '../../stores/projects';

/** Props for {@link ActiveProjectSync}. */
interface ActiveProjectSyncProps {
  /** The projects store whose active project tracks the URL. */
  projects: IProjects;
}

/**
 * Side-effect helper replacing the v5 `<Route children>` render-prop that kept
 * the MST "active project" in sync with the URL. v7 has no always-rendered
 * children render-prop, so this reads the current match for `/project/:id` and
 * sets (or clears) the active project.
 *
 * The MST mutation runs in a `useEffect`, not in the render body: under React
 * 18 calling `projects.setActiveProject(...)` during render mutates observables
 * mid-render and triggers "Cannot update during an existing state transition".
 * The effect runs after commit, so the active project still tracks the URL but
 * without the render-phase write. Renders nothing.
 */
const ActiveProjectSync = ({ projects }: ActiveProjectSyncProps) => {
  const match = useMatch('/project/:id');
  // `match.params.id` is `string | undefined` on the typed router; a matched
  // `/project/:id` route always yields the string id, so narrow to the store's
  // `string | null` contract at this boundary (behaviour unchanged).
  const id = (match ? match.params.id : null) as string | null;
  useEffect(() => {
    projects.setActiveProject(id);
  }, [projects, id]);
  return null;
};

/**
 * The main layout of the app.
 *
 * If the projects are not initialized: render the AppLoader subcomponent.
 *
 * Else renders the AppBar and one of (depending on the route):
 * - Account
 * - Project
 * - Dashboard
 *
 * The Projects store was injected with mobx-react (`inject(({ store: { projects
 * } }) => ({ projects }))`); it now comes from useStores(). The class was also
 * wrapped in withRouter but never read any router prop, so the shim is dropped.
 */
export default observer(function AppLayout() {
  const store = useStores();
  // The projects node IS the hand-declared `IProjects`; only the loose MST
  // instance widening differs (e.g. `IModelVersion.undo()`), so read it as the
  // interface at this single boundary (same pattern as the metamodel access).
  const projects = store.projects as unknown as IProjects;

  if (!projects.initialized) {
    return <AppLoader projects={projects} />;
  }

  return (
    <div className="bg-light h-screen">
      {/*
        relative z-20: the More menu is position:absolute and hangs into the
        project view. reactsymbols `.Select` (the FSM name) is z-index 10, so
        without a higher stacking context on this header the name paints over
        the dropdown on a small window.
      */}
      <div className="relative z-20 h-[60px]">
        <AppBar />
      </div>
      <div className="h-[calc(100vh-60px)]">
        <Routes>
          <Route path="account" element={<Account />} />
          <Route path="project/:id" element={<Project />} />
          {/*
            Catch-all (not `index`): the v5 content `<Switch>` ended in
            `<Route path="/" component={Dashboard}>` with no `exact`, so it
            prefix-matched ANY unmatched path and rendered the Dashboard.
            `index` only matches the empty subpath, leaving leftover
            subpaths blank, so use `path="*"` to keep the v5 SPA catch-all.
          */}
          <Route path="*" element={<Dashboard />} />
        </Routes>
        {/* Keep the active project in MST in sync with the URL */}
        <ActiveProjectSync projects={projects} />
      </div>
    </div>
  );
});
